import { useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Textarea } from "../../components/ui/Textarea";
import { LoadingSpinner } from "../../components/ui/LoadingSpinner";
import Badge from "../../components/ui/Badge";
import { Mail, Phone, MapPin, MessageSquare, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import styled from "styled-components";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    left: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    right: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  max-width: 1152px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 80px;
  }
`;

const FormSection = styled.div``;

const FormTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h2};
  font-weight: 700;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.status?.redText || "#dc2626"};
  margin: 4px 0 0 0;
`;

const SubmitError = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.status?.redText || "#dc2626"};
  margin: 0 0 16px 0;
`;

const SuccessMessage = styled.div`
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}10`};
  border: 1px solid ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}20`};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const SuccessTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h3};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const SuccessText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const InfoSection = styled.div``;

const InfoTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h2};
  font-weight: 700;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const InfoCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 48px;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const InfoIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
`;

const InfoContent = styled.div``;

const InfoCardTitle = styled.h3`
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const InfoCardDetail = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  margin: 0 0 4px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const InfoCardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const QuickResponseCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.glass?.light?.background || "hsla(0, 0%, 100%, 0.8)"};
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}20`};

  body.dark & {
    background: ${({ theme }) => theme.colors.glass?.dark?.background || "hsla(240, 15%, 10%, 0.8)"};
    border-color: ${({ theme }) => `${theme.colors.dark?.primary || theme.colors.primary.light}20`};
  }
`;

const QuickResponseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const QuickResponseTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const QuickResponseIcon = styled(Clock)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const QuickResponseText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 16px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FAQSection = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const FAQHeader = styled.div`
  max-width: 768px;
  margin: 0 auto 48px;
  text-align: center;
`;

const FAQTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FAQDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FAQList = styled.div`
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const FAQQuestion = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FAQAnswer = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const contactInfo = [
  {
    icon: Mail,
    title: "Email",
    details: "hello@kliqcrm.com",
    description: "We'll respond within 24 hours",
  },
  {
    icon: Phone,
    title: "Phone",
    details: "+1 (555) 123-4567",
    description: "Mon-Fri from 9am to 6pm EST",
  },
  {
    icon: MapPin,
    title: "Office",
    details: "San Francisco, CA",
    description: "123 Innovation Street",
  },
];

const faqs = [
  {
    question: "How quickly can I get started?",
    answer: "You can sign up and start using Kliq CRM in minutes. No credit card required for the free trial.",
  },
  {
    question: "Do you offer onboarding support?",
    answer: "Yes! All paid plans include dedicated onboarding support to help you get the most out of Kliq CRM.",
  },
  {
    question: "Can I migrate from another platform?",
    answer: "Absolutely. We offer free migration assistance and can import data from most major platforms.",
  },
];

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://kliqplatform.vercel.app';
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to send message');
      }
      setIsSubmitted(true);
      setFormData({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      setErrors({ submit: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Us"
        description="Get in touch with the Kliq CRM team. We're here to help you with any questions about our platform."
        keywords="contact, support, help, Kliq CRM, SaaS"
        hreflang
      />
      <PageTransition>
        {/* Hero */}
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Contact</Badge>
              </BadgeWrapper>
              <Title>
                Get in <GradientText>touch</GradientText>
              </Title>
              <Description>
                Have a question or want to learn more? We'd love to hear from you. Our team is here to help.
              </Description>
            </HeroContent>
          </Container>
        </Section>

        {/* Contact Section */}
        <Section>
          <Container>
            <ContactGrid>
              {/* Form */}
              <FormSection>
                <FormTitle>Send us a message</FormTitle>

                {isSubmitted ? (
                  <SuccessMessage>
                    <SuccessIcon>
                      <CheckCircle2 size={32} style={{ color: "hsl(258, 90%, 58%)" }} />
                    </SuccessIcon>
                    <SuccessTitle>Message Sent!</SuccessTitle>
                    <SuccessText>
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </SuccessText>
                  </SuccessMessage>
                ) : (
                    <Form onSubmit={handleSubmit}>
                    {errors.submit && <SubmitError>{errors.submit}</SubmitError>}
                    <FormRow>
                      <FormGroup>
                        <Label>Name *</Label>
                        <Input
                          name="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                      </FormGroup>
                      <FormGroup>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          name="email"
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <Label>Company</Label>
                      <Input
                        name="company"
                        placeholder="Your company name"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Message *</Label>
                      <Textarea
                        name="message"
                        placeholder="Tell us how we can help..."
                        value={formData.message}
                        onChange={handleChange}
                        style={{ minHeight: "150px" }}
                      />
                      {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                    </FormGroup>
                    <Button variant="hero" size="lg" type="submit" disabled={isSubmitting} fullWidth>
                      {isSubmitting ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight size={20} />
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </FormSection>

              {/* Info */}
              <InfoSection>
                <InfoTitle>Other ways to reach us</InfoTitle>

                <InfoCards>
                  {contactInfo.map((info) => (
                    <InfoCard key={info.title}>
                      <InfoIconBox>
                        <info.icon size={20} />
                      </InfoIconBox>
                      <InfoContent>
                        <InfoCardTitle>{info.title}</InfoCardTitle>
                        <InfoCardDetail>{info.details}</InfoCardDetail>
                        <InfoCardDescription>{info.description}</InfoCardDescription>
                      </InfoContent>
                    </InfoCard>
                  ))}
                </InfoCards>

                {/* Quick Response */}
                <QuickResponseCard>
                  <QuickResponseHeader>
                    <QuickResponseIcon />
                    <QuickResponseTitle>Quick Response</QuickResponseTitle>
                  </QuickResponseHeader>
                  <QuickResponseText>
                    Need immediate help? Our support team typically responds within 2 hours during business hours.
                  </QuickResponseText>
                  <Button variant="outline" fullWidth>
                    <MessageSquare size={16} />
                    Start Live Chat
                  </Button>
                </QuickResponseCard>
              </InfoSection>
            </ContactGrid>
          </Container>
        </Section>

        {/* FAQ */}
        <FAQSection>
          <Container>
            <FAQHeader>
              <FAQTitle>Common Questions</FAQTitle>
              <FAQDescription>Quick answers to help you get started.</FAQDescription>
            </FAQHeader>

            <FAQList>
              {faqs.map((faq) => (
                <FAQItem key={faq.question}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <FAQAnswer>{faq.answer}</FAQAnswer>
                </FAQItem>
              ))}
            </FAQList>
          </Container>
        </FAQSection>
      </PageTransition>
    </Layout>
  );
}
